import _, { uniqBy } from "lodash"
import { RESOLUTIONS, getInitialTimeStamps } from "../utils/chart-helpers"
import { Canvg } from "canvg"
import jsPDF from "jspdf"
import { AnyDataPlan, ReportData, ReportOptions, ReportType } from "."
import { DataPlanModel, SharedDataPlan, SingleDataPlan, TerminalModel, TerminalType } from "../utils/models"
import { ChartSettings } from "../components/organisms/charts/SharedDataChartView"
import { REPORT_FONT_FACES } from "./components/ReportText"

/** size of an a4 in pixels. */
export const REPORT_SIZE = [210, 297]
export const REPORT_SIZE_SCALING = 2.5

/**
 * Returns a promise that resolves to a blob URI of a pdf from the given element.
 */
export const renderHtmlToPdfBlobUri = (el: HTMLElement) => {
  return new Promise<string>((resolve) => {
    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
    })

    doc.html(el, {
      x: 0,
      y: 0,
      width: REPORT_SIZE[0],
      windowWidth: REPORT_SIZE[0] * REPORT_SIZE_SCALING,
      fontFaces: REPORT_FONT_FACES,
      async callback(doc) {
        resolve(doc.output("bloburi").toString())
      },
    })
  })
}

/**
 * Returns a promise that resolves to a data URL of the given SVG as a png.
 */
export const renderSvgMarkupToDataUrl = async (svg: string, width: number, height: number) => {
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height

  const canvg = Canvg.fromString(canvas.getContext("2d")!, svg)
  await canvg.render({ ignoreAnimation: true })

  return canvas.toDataURL("image/png", 1)
}

export const isSharedDataPlan = <T extends SingleDataPlan | SharedDataPlan>(
  dataPlan: T,
): dataPlan is Extract<T, SharedDataPlan> => {
  return dataPlan.__typename === "SharedDataPlanType"
}

export const dataPlanToReportData = (
  dataPlan: ReportType<ReportData, "dataplan">["dataPlan"],
): ReportType<ReportData, "dataplan"> => {
  return isSharedDataPlan(dataPlan)
    ? {
        type: "dataplan",
        sharedPlan: true,
        dataPlan,
      }
    : {
        type: "dataplan",
        sharedPlan: false,
        dataPlan,
      }
}

export const reportTerminals = (
  data: ReportData,
): { terminal: TerminalType | TerminalModel; plan?: SingleDataPlan | SharedDataPlan }[] => {
  switch (data.type) {
    case "dataplan":
      return data.sharedPlan
        ? (data.dataPlan.sharedInstances as SingleDataPlan[]).map((sharedInstance) => ({
            terminal: sharedInstance.terminal.terminal,
            plan: sharedInstance,
          }))
        : [
            {
              terminal: data.dataPlan.terminal.terminal,
              plan: data.dataPlan,
            },
          ]
    case "group":
      return (data.group.terminals?.map((terminal) => terminal) ?? [])
        .filter((terminal) => terminal !== undefined)
        .map((terminal) => ({
          terminal,
          plan: terminal.apiTerminal?.latestDataPlan as any,
        }))
    case "terminal":
      return data.terminals
        .filter((terminal) => terminal !== undefined)
        .map((terminal) => ({
          terminal,
          plan: terminal.apiTerminal?.latestDataPlan as any,
        }))
  }
}

export const reportDataPlans = (data: ReportData): AnyDataPlan[] => {
  switch (data.type) {
    case "dataplan":
      return [data.dataPlan]
    case "group":
      return uniqBy(
        data.group.terminals
          ?.map((terminal) => terminal.apiTerminal?.latestDataPlan)
          .filter((dataPlan) => dataPlan != undefined),
        "id",
      ) as AnyDataPlan[]
    case "terminal":
      return uniqBy(
        data.terminals
          .map((terminal) => terminal.apiTerminal?.latestDataPlan)
          .filter((dataPlan) => dataPlan != undefined),
        "id",
      ) as AnyDataPlan[]
  }
}

export const defaultChartSettings = (): ChartSettings => ({
  ...getInitialTimeStamps(),
  resolution: RESOLUTIONS.year,
})

export const dataPlanReportOptions = (
  dataPlan: DataPlanModel,
  chartSettings: ChartSettings,
): ReportType<ReportOptions, "dataplan"> => {
  return {
    type: "dataplan",
    apiPlanId: dataPlan.apiPlanId,
    sharedPlan: dataPlan.sharedPlan,
    ...chartSettings,
  }
}

export const groupReportOptions = (groupId: number): ReportOptions => {
  return {
    type: "group",
    ...defaultChartSettings(),
    groupId,
  }
}

export const terminalReportOptions = (apiTerminalIds: number[]): ReportOptions => {
  return {
    type: "terminal",
    ...defaultChartSettings(),
    apiTerminalIds,
  }
}

export const terminalCombinedDataUsage = (terminal: TerminalModel | TerminalType, parentPlanId: string) =>
  terminal.apiTerminal?.singlePlans
    ?.filter((singlePlan) => singlePlan.parentSharedPlan?.id === parentPlanId)
    .reduce((total, singlePlan) => (singlePlan.totalDataUsage ?? 0) + total, 0)
